import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getRuns, getRunsAggregate, getRunById, createRunRerun } from '../hooks/runs';
import { RunScope, LogsSortBy, PeriodPreset, PeriodState, MAX_ANALYTICS_WINDOW_DAYS } from '../hooks/dto/runItem.dto';
import { SearchRunsResponseDTO } from '@src/services/automations/modules/v1/requests/dto/requests.search.response.dto';
import { AggregateRunsResponseDTO } from '@src/services/automations/modules/v1/requests/dto/requests.aggregate.response.dto';
import { RequestGetByIdResponseDTO } from '@src/services/automations/modules/v1/requests/dto/requests.getById.response.dto';
import { getOrganizationsNames } from '@src/pages/Tasks/hooks/organizations';

const PAGE_SIZE = 25;
const DAY = 24 * 60 * 60 * 1000;

const PRESET_DAYS: Record<Exclude<PeriodPreset, 'custom'>, number> = { '24h': 1, '7d': 7, '30d': 30 };

const DEFAULT_PRESET: Exclude<PeriodPreset, 'custom'> = '24h';

function presetToRange(preset: Exclude<PeriodPreset, 'custom'>): { since: string; until: string } {
  const until = new Date();
  const since = new Date(until.getTime() - PRESET_DAYS[preset] * DAY);
  return { since: since.toISOString(), until: until.toISOString() };
}

interface Ctx {
  scope: RunScope;
  setScope: (s: RunScope) => void;
  organizationId?: string;
  setOrganizationId: (id?: string) => void;
  flowName?: string;
  flowType?: 'CUSTOM' | 'CANONICAL';
  setAutomation: (key: string, type: 'CUSTOM' | 'CANONICAL') => void;
  organizationNames: Record<string, string>;
  period: PeriodState;
  setPreset: (p: Exclude<PeriodPreset, 'custom'>) => void;
  setCustomRange: (start: Date, end: Date) => void;
  periodError: string | null;
  aggregate: AggregateRunsResponseDTO | null;
  runs: SearchRunsResponseDTO | null;
  isLoading: boolean;
  error: string | null;
  isLoadingRuns: boolean;
  logsError: string | null;
  logsPage: number;
  setLogsPage: (p: number) => void;
  logsSort: { by: LogsSortBy; dir: 'asc' | 'desc' };
  setLogsSort: (by: LogsSortBy, dir: 'asc' | 'desc') => void;
  contextSearch: string;
  setContextSearch: (term: string) => void;
  pageSize: number;
  selectedRunId: string | null;
  setSelectedRunId: (id: string | null) => void;
  selectedRun: RequestGetByIdResponseDTO | null;
  isLoadingRun: boolean;
  createRerun: () => Promise<void>;
}

const RunsContext = createContext<Ctx | undefined>(undefined);

export function RunsProvider({ children }: { children: React.ReactNode }) {
  const [searchParams] = useSearchParams();
  const [scope, setScope] = useState<RunScope>('all');
  const [organizationId, setOrgId] = useState<string | undefined>();
  const [flowName, setFlowName] = useState<string | undefined>();
  const [flowType, setFlowType] = useState<'CUSTOM' | 'CANONICAL' | undefined>();
  const [organizationNames, setOrganizationNames] = useState<Record<string, string>>({});
  const [period, setPeriod] = useState<PeriodState>({ preset: DEFAULT_PRESET, ...presetToRange(DEFAULT_PRESET) });
  const [periodError, setPeriodError] = useState<string | null>(null);

  const [aggregate, setAggregate] = useState<AggregateRunsResponseDTO | null>(null);
  const [runs, setRuns] = useState<SearchRunsResponseDTO | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingRuns, setIsLoadingRuns] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);

  const [logsPage, setLogsPage] = useState(1);
  const [logsSort, setLogsSortState] = useState<{ by: LogsSortBy; dir: 'asc' | 'desc' }>({
    by: 'createdAt',
    dir: 'desc',
  });
  const [contextSearch, setContextSearchState] = useState('');

  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [selectedRun, setSelectedRun] = useState<RequestGetByIdResponseDTO | null>(null);
  const [isLoadingRun, setIsLoadingRun] = useState(false);

  useEffect(() => {
    getOrganizationsNames()
      .then(setOrganizationNames)
      .catch(() => setOrganizationNames({}));
  }, []);

  // deep-link: ?runId=<requestId> opens the drawer
  useEffect(() => {
    const rid = searchParams.get('runId');
    if (rid) setSelectedRunId(rid);
  }, [searchParams]);

  const setOrganizationId = useCallback((id?: string) => {
    setOrgId((prev) => {
      if (prev !== id) {
        setFlowName(undefined);
        setFlowType(undefined);
        setContextSearchState('');
      }
      return id;
    });
    setScope(id ? 'org' : 'all');
    setLogsPage(1);
  }, []);
  const setAutomation = useCallback((key: string, type: 'CUSTOM' | 'CANONICAL') => {
    setFlowName(key);
    setFlowType(type);
    setScope('auto');
    setLogsPage(1);
  }, []);
  const setPreset = useCallback((p: Exclude<PeriodPreset, 'custom'>) => {
    setPeriod({ preset: p, ...presetToRange(p) });
    setPeriodError(null);
    setLogsPage(1);
  }, []);
  const setCustomRange = useCallback((start: Date, end: Date) => {
    if (end.getTime() - start.getTime() > MAX_ANALYTICS_WINDOW_DAYS * DAY) {
      setPeriodError(`Intervalo máximo de ${MAX_ANALYTICS_WINDOW_DAYS} dias para os gráficos.`);
      return;
    }
    setPeriodError(null);
    setPeriod({ preset: 'custom', since: start.toISOString(), until: end.toISOString() });
    setLogsPage(1);
  }, []);
  const setLogsSort = useCallback((by: LogsSortBy, dir: 'asc' | 'desc') => {
    setLogsSortState({ by, dir });
    setLogsPage(1);
  }, []);
  const setContextSearch = useCallback((term: string) => {
    setContextSearchState(term.trim());
    setLogsPage(1);
  }, []);

  useEffect(() => {
    setError(null);
    if (scope === 'all' || !organizationId) {
      setAggregate(null);
      setIsLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const agg = await getRunsAggregate({
          organizationId,
          since: period.since,
          until: period.until,
          groupBy: 'both',
        });
        if (!cancelled) setAggregate(agg);
      } catch {
        if (!cancelled) setError('Falha ao carregar analytics.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scope, organizationId, period.since, period.until]);

  // fetch logs (search) for org/auto scopes with pagination + sort
  useEffect(() => {
    if (scope === 'all' || !organizationId) {
      setRuns(null);
      setLogsError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoadingRuns(true);
      setLogsError(null);
      try {
        const res = await getRuns({
          organizationId,
          since: period.since,
          until: period.until,
          flowName: scope === 'auto' && flowType === 'CUSTOM' ? flowName : undefined,
          canonicalFlowKey: scope === 'auto' && flowType === 'CANONICAL' ? flowName : undefined,
          status: 'all',
          contextSearch: contextSearch || undefined,
          limit: PAGE_SIZE,
          offset: (logsPage - 1) * PAGE_SIZE,
          sortBy: logsSort.by,
          sortDir: logsSort.dir,
        });
        if (!cancelled) setRuns(res);
      } catch {
        if (!cancelled) {
          setRuns(null);
          setLogsError(
            contextSearch
              ? 'A busca no contexto falhou ou demorou demais. Reduza o período ou filtre por uma automação.'
              : 'Falha ao carregar execuções.',
          );
        }
      } finally {
        if (!cancelled) setIsLoadingRuns(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    scope,
    organizationId,
    flowName,
    flowType,
    period.since,
    period.until,
    logsPage,
    logsSort.by,
    logsSort.dir,
    contextSearch,
  ]);

  // fetch selected run detail
  useEffect(() => {
    if (!selectedRunId) {
      setSelectedRun(null);
      return;
    }
    let cancelled = false;
    setIsLoadingRun(true);
    getRunById(selectedRunId)
      .then((r) => {
        if (!cancelled) setSelectedRun(r);
      })
      .catch(() => {
        if (!cancelled) setSelectedRun(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingRun(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedRunId]);

  const createRerun = useCallback(async () => {
    if (selectedRunId) await createRunRerun(selectedRunId);
  }, [selectedRunId]);

  const value = useMemo<Ctx>(
    () => ({
      scope,
      setScope,
      organizationId,
      setOrganizationId,
      flowName,
      flowType,
      setAutomation,
      organizationNames,
      period,
      setPreset,
      setCustomRange,
      periodError,
      aggregate,
      runs,
      isLoading,
      error,
      isLoadingRuns,
      logsError,
      logsPage,
      setLogsPage,
      logsSort,
      setLogsSort,
      contextSearch,
      setContextSearch,
      pageSize: PAGE_SIZE,
      selectedRunId,
      setSelectedRunId,
      selectedRun,
      isLoadingRun,
      createRerun,
    }),
    [
      scope,
      organizationId,
      flowName,
      flowType,
      organizationNames,
      period,
      periodError,
      aggregate,
      runs,
      isLoading,
      error,
      isLoadingRuns,
      logsError,
      logsPage,
      logsSort,
      contextSearch,
      selectedRunId,
      selectedRun,
      isLoadingRun,
      setOrganizationId,
      setAutomation,
      setPreset,
      setCustomRange,
      setLogsSort,
      setContextSearch,
      createRerun,
    ],
  );

  return <RunsContext.Provider value={value}>{children}</RunsContext.Provider>;
}

export function useRunsContext(): Ctx {
  const ctx = useContext(RunsContext);
  if (!ctx) throw new Error('useRunsContext must be used within RunsProvider');
  return ctx;
}
